import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ADMIN_NAV, COMPANION_NAV } from "./nav-config";
import { useApp } from "@/context/AppContext";
import { Logo } from "@/components/brand/Logo";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuRadioGroup, DropdownMenuRadioItem } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { useApi, apiPost } from "@/lib/api";
import { toast } from "sonner";
import { Menu, Palette, UserCog, RefreshCw, Rows3, MoveVertical, Sparkles, ChevronDown, LayoutGrid, LogOut } from "lucide-react";

const DEPARTMENTS = ["all", "Engineering", "Finance", "Legal", "Sales", "Marketing", "HR", "Operations", "Customer Support"];
const THEME_LABELS = { "theme-paper": "Paper / Editorial", "theme-navy": "Navy Chrome", "theme-dark": "Slate / Dark" };

function NavList({ items, onNavigate }) {
  const { pathname } = useLocation();
  const { role } = useApp();
  const visible = items.filter((i) => !i.roles || i.roles.includes(role.key));
  return (
    <nav className="space-y-0.5 px-2">
      {visible.map((item) => {
        const active = pathname === item.path;
        const Icon = item.icon;
        return (
          <Link key={item.path} to={item.path} onClick={onNavigate} data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={cn("group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors relative",
              active ? "bg-nav-text/10 text-nav-text font-semibold" : "text-nav-text/70 hover:bg-nav-text/5 hover:text-nav-text")}>
            {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-full bg-gold" />}
            <Icon className="h-4 w-4 shrink-0" />
            <span className="truncate">{item.label}</span>
            {item.module && <span className="ml-auto text-[10px] mono text-nav-text/40">{item.module}</span>}
          </Link>
        );
      })}
    </nav>
  );
}

export function AppShell({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme, density, setDensity, reduceMotion, setReduceMotion, role, changeRole, scopeDept, setScopeDept, user, logout } = useApp();
  const workspace = pathname.startsWith("/companion") ? "companion" : "admin";
  const items = workspace === "companion" ? COMPANION_NAV : ADMIN_NAV;
  const { data: roles = [] } = useApi("roles", "/roles");
  const [mobileOpen, setMobileOpen] = useState(false);

  const resetSeed = async () => {
    try { await apiPost("/admin/seed/reset"); toast.success("Seed data reset to baseline"); setTimeout(() => window.location.reload(), 700); }
    catch { toast.error("Reset failed"); }
  };

  const Sidebar = (
    <div className="flex h-full flex-col bg-nav-chrome">
      <div className="flex items-center gap-2 px-4 h-16 border-b border-nav-text/10">
        <div className="rounded-lg bg-paper px-2.5 py-1.5 shadow-sm"><Logo className="h-7" /></div>
      </div>
      <div className="px-3 py-3">
        <div className="flex rounded-lg bg-nav-text/10 p-1 text-xs">
          <button onClick={() => navigate("/admin/cockpit")} data-testid="workspace-admin" className={cn("flex-1 rounded-md py-1.5 font-medium transition-colors", workspace === "admin" ? "bg-nav-text text-nav-chrome" : "text-nav-text/70")}>Admin Console</button>
          <button onClick={() => navigate("/companion/home")} data-testid="workspace-companion" className={cn("flex-1 rounded-md py-1.5 font-medium transition-colors", workspace === "companion" ? "bg-nav-text text-nav-chrome" : "text-nav-text/70")}>Companion</button>
        </div>
      </div>
      <ScrollArea className="flex-1 thin-scroll"><div className="pb-6"><NavList items={items} onNavigate={() => setMobileOpen(false)} /></div></ScrollArea>
      <div className="border-t border-nav-text/10 p-3 space-y-1">
        <button onClick={() => navigate("/os")} className="flex w-full items-center gap-2 text-xs text-nav-text/70 hover:text-nav-text px-1 py-1" data-testid="nav-desktop"><LayoutGrid className="h-3.5 w-3.5" />Desktop</button>
        <button onClick={() => { logout(); navigate("/login"); }} className="flex w-full items-center gap-2 text-xs text-nav-text/70 hover:text-nav-text px-1 py-1" data-testid="nav-signout"><LogOut className="h-3.5 w-3.5" />Sign out{user ? ` (${user.name.split(" ")[0]})` : ""}</button>
      </div>
    </div>
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div className="navitra-bg min-h-screen flex">
        <img src="/brand/navitra-logo.webp" alt="" className="brand-watermark hidden lg:block" aria-hidden />
        <aside className="hidden lg:flex w-64 shrink-0 sticky top-0 h-screen">{Sidebar}</aside>
        <div className="flex-1 min-w-0 relative z-[1]">
          <header className="sticky top-0 z-20 flex items-center gap-2 h-16 border-b bg-background/85 backdrop-blur px-4 md:px-6">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild><Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu"><Menu className="h-5 w-5" /></Button></SheetTrigger>
              <SheetContent side="left" className="p-0 w-64">{Sidebar}</SheetContent>
            </Sheet>
            <div className="hidden md:flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Scope</span>
              <Select value={scopeDept} onValueChange={setScopeDept}>
                <SelectTrigger className="h-8 w-[150px]" data-testid="scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d === "all" ? "All departments" : d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              <Tooltip><TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={resetSeed} data-testid="reset-seed-btn" className="hidden sm:inline-flex"><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Reset data</Button>
              </TooltipTrigger><TooltipContent>Reset seeded demo data to baseline</TooltipContent></Tooltip>

              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid="theme-switcher"><Palette className="h-4 w-4" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Theme</DropdownMenuLabel>
                  <DropdownMenuRadioGroup value={theme} onValueChange={setTheme}>
                    {Object.entries(THEME_LABELS).map(([k, v]) => <DropdownMenuRadioItem key={k} value={k} data-testid={`theme-${k}`}>{v}</DropdownMenuRadioItem>)}
                  </DropdownMenuRadioGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Density</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => setDensity(density === "compact" ? "comfortable" : "compact")} data-testid="density-toggle">
                    {density === "compact" ? <Rows3 className="h-4 w-4 mr-2" /> : <MoveVertical className="h-4 w-4 mr-2" />}{density === "compact" ? "Comfortable" : "Compact"} density
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setReduceMotion(!reduceMotion)} data-testid="reduce-motion">
                    <Sparkles className="h-4 w-4 mr-2" />{reduceMotion ? "Enable" : "Reduce"} motion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <div className="flex items-center gap-1.5 rounded-md border bg-card px-2.5 py-1.5 text-sm" data-testid="current-role"><UserCog className="h-4 w-4 text-navy" /><span className="hidden sm:inline max-w-[150px] truncate">{user?.name || role.name}</span></div>
            </div>
          </header>
          <main className="relative z-[1] p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">{children}</main>
        </div>
      </div>
    </TooltipProvider>
  );
}
